import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMyTickets } from '../utils/api';
import { API_BASE_URL } from '../constants/api';

// ─── Pre-made FAQ answers ────────────────────────────────────────────────────
const FAQ = [
  // ── Account ──────────────────────────────────────────────────────────────
  {
    keywords: ['delete', 'remove account', 'close account', 'deactivate', 'erase account'],
    answer: 'To delete your account go to Settings → Account → Delete Account. This is permanent and cannot be undone.\n\nYou can also visit vibana.app/delete-account for step-by-step instructions. Need help? Email support@vibana.app.',
  },
  {
    keywords: ['reset password', 'forgot password', 'change password', 'cant login', "can't login", 'lost password', 'password', 'locked out'],
    answer: 'To reset your password:\n1. Go to the login page\n2. Tap "Forgot password"\n3. Enter your email address\n4. Check your inbox for a reset link\n\nIf the email doesn\'t arrive check your spam folder or email support@vibana.app.',
  },
  {
    keywords: ['change email', 'update email', 'change phone', 'update phone', 'change name', 'update profile', 'edit profile', 'update my'],
    answer: 'To update your profile info go to Settings → Profile and edit your name, email, phone, or city.\n\nIf you change your email you will receive a verification link at the new address to confirm the change.',
  },
  {
    keywords: ['verify email', 'email verification', 'resend verification', 'confirm email', 'not verified', 'verify my'],
    answer: 'Check your inbox for a verification email from Vibana — it may be in your spam folder.\n\nTo resend: go to your profile or click "Resend verification" on the banner at the top of the page.\n\nStill not receiving it? Email support@vibana.app.',
  },
  {
    keywords: ['create account', 'sign up', 'register', 'how to join', 'new account', 'make account'],
    answer: 'To create a Vibana account go to vibana.app/register or tap "Get started" at the top of the page. You can sign up with your email or continue with Google/Apple.',
  },
  {
    keywords: ['login', 'sign in', 'log in', 'cant sign in', "can't sign in", 'access my account'],
    answer: 'To log in go to vibana.app/login and enter your email and password. If you\'ve forgotten your password tap "Forgot password" to reset it.\n\nIf you signed up with Google or Apple use those buttons to log in.',
  },
  {
    keywords: ['profile', 'my profile', 'account settings', 'account page', 'my account'],
    answer: 'Your profile is at vibana.app/profile. From there you can view your tickets, saved events, update your info, and manage your account settings.',
  },

  // ── Tickets ───────────────────────────────────────────────────────────────
  {
    keywords: ['find my ticket', 'where is my ticket', 'access ticket', 'view ticket', 'download ticket', 'qr code', 'show ticket', 'see my ticket', 'my ticket'],
    answer: 'Go to My Tickets (tap your profile icon → My Tickets, or visit vibana.app/profile) to see all your purchased tickets.\n\nEach ticket has a QR code — screenshot it or show it from the app at the door. You should also receive a confirmation email.',
  },
  {
    keywords: ['buy ticket', 'purchase ticket', 'how to get ticket', 'how to buy', 'get tickets', 'ticket price'],
    answer: 'To buy a ticket:\n1. Find the event on Vibana\n2. Tap "Get Tickets"\n3. Select your ticket type\n4. Complete payment with your card\n\nYour ticket will be emailed to you and saved in My Tickets.',
  },
  {
    keywords: ['ticket not received', 'no ticket email', 'where is my confirmation', 'didnt receive ticket', "didn't receive ticket"],
    answer: 'Check your spam/junk folder first. Tickets are emailed from noreply@vibana.app.\n\nYou can also find all your tickets at vibana.app/profile → My Tickets — they\'re always there even if the email went missing.',
  },
  {
    keywords: ['transfer ticket', 'give ticket', 'send ticket to friend', 'transfer my ticket'],
    answer: 'Ticket transfers are managed by the event organizer. Contact the organizer directly or email support@vibana.app with your ticket ID and the recipient\'s details and we\'ll do our best to help.',
  },

  // ── Refunds ───────────────────────────────────────────────────────────────
  {
    keywords: ['refund', 'money back', 'cancel ticket', 'refund policy', 'get refund', 'return ticket'],
    answer: "Happy to help with your refund request!\n\nRefund eligibility depends on the event organizer's policy:\n\n• No Refund — Ticket sales are final\n• 48-Hour Policy — Refundable up to 48 hours before the event\n• 7-Day Policy — Refundable up to 7 days before the event\n• Full Refund — Fully refundable at any time\n\nTo request a refund:\n1. Go to your Profile → My Tickets\n2. Find the ticket and tap 'Request Refund'\n3. Follow the steps to submit your request\n\nYou can also visit vibana.app/refund-request directly.\n\nIf your event was cancelled, refunds are processed automatically within 5–10 business days.\n\nStill need help? Type 'ticket' and I'll create a support request for our team!",
  },
  {
    keywords: ['event cancelled', 'event canceled', 'event postponed', 'event rescheduled'],
    answer: "We're sorry to hear your event was cancelled or postponed.\n\nIf the event was cancelled:\n• Refunds are typically issued automatically within 5–10 business days to your original payment method\n• You'll receive an email confirmation once the refund is processed\n\nIf the event was postponed or rescheduled:\n• Check the updated event page for the new date\n• If the new date doesn't work for you, you may be eligible for a refund — check your ticket in Profile → My Tickets\n\nHaven't received a refund after 10 days? Type 'ticket' and I'll escalate this to our team right away!",
  },

  // ── Payment & Billing ─────────────────────────────────────────────────────
  {
    keywords: ['payment failed', 'card declined', 'payment not working', 'charge failed', 'payment error'],
    answer: 'If your payment failed:\n1. Check your card details and billing address are correct\n2. Make sure your bank hasn\'t blocked the charge\n3. Try a different card\n\nIf a charge appeared on your bank statement but you got no ticket, email support@vibana.app immediately with the amount and date.',
  },
  {
    keywords: ['receipt', 'invoice', 'billing', 'charged', 'charge', 'payment confirmation'],
    answer: 'Your payment confirmation and receipt are emailed to you immediately after purchase.\n\nYou can also find receipts in My Tickets → select the ticket → View Receipt.',
  },
  {
    keywords: ['promo code', 'discount code', 'coupon', 'voucher', 'discount'],
    answer: 'Enter your promo or discount code at checkout — there\'s a "Promo code" field on the payment page.\n\nIf your code isn\'t working check it hasn\'t expired or reached its usage limit. Still having trouble? Email support@vibana.app.',
  },

  // ── Events ────────────────────────────────────────────────────────────────
  {
    keywords: ['find event', 'search event', 'discover event', 'upcoming events', 'events near me', 'what events'],
    answer: 'Browse all events at vibana.app/events. You can filter by city, date, and category to find exactly what you\'re looking for.',
  },
  {
    keywords: ['list event', 'post event', 'submit event', 'add event', 'create event', 'become organizer', 'host event'],
    answer: 'To list an event on Vibana:\n1. Go to your profile → Become an Organizer (if you haven\'t already)\n2. Once approved, go to Dashboard → Create Event\n\nYou can also email organizers@vibana.app for faster onboarding.',
  },
  {
    keywords: ['event info', 'event details', 'event location', 'event time', 'event date', 'event address'],
    answer: 'Full event details (time, location, what to bring, age restrictions) are on the event page. Tap any event on Vibana to see everything.\n\nIf information is missing or incorrect please contact the organizer directly or email support@vibana.app.',
  },

  // ── Organizer ─────────────────────────────────────────────────────────────
  {
    keywords: ['organizer', 'organiser', 'dashboard', 'manage event', 'organizer account', 'organizer help'],
    answer: 'Organizers can manage events, view analytics, and handle ticketing from the Organizer Dashboard at vibana.app/dashboard.\n\nFor onboarding help email organizers@vibana.app or type "ticket" here to open a support request.',
  },
  {
    keywords: ['payout', 'payment out', 'receive money', 'when do i get paid', 'earnings', 'revenue'],
    answer: 'Organizer payouts are processed after the event. Typical timeline is 3–5 business days post-event.\n\nFor payout questions email organizers@vibana.app with your event name and organizer account email.',
  },

  // ── General ───────────────────────────────────────────────────────────────
  {
    keywords: ['what is vibana', 'about vibana', 'how does vibana work', 'vibana app'],
    answer: 'Vibana is an event discovery and ticketing platform for African events & community across North America. Find concerts, parties, cultural events, and more — then buy tickets directly on the platform.',
  },
  {
    keywords: ['contact support', 'talk to someone', 'speak to', 'email support', 'contact us', 'help desk', 'reach you'],
    answer: 'You can reach our team at support@vibana.app. We typically respond within 24 hours.\n\nOr type "ticket" here and I\'ll create a support ticket for you right now.',
  },
  {
    keywords: ['privacy', 'data', 'gdpr', 'personal data', 'privacy policy', 'my data'],
    answer: 'Read Vibana\'s full Privacy Policy at vibana.app/privacy. We take your data seriously and never sell your personal information.\n\nFor data-related requests email support@vibana.app.',
  },
  {
    keywords: ['terms', 'terms of service', 'terms and conditions', 'rules', 'user agreement'],
    answer: 'Read Vibana\'s Terms of Service at vibana.app/terms. These cover your rights and responsibilities as a user, ticket buyer, or organizer.',
  },
];

const TICKET_CATEGORIES = [
  'Ticket issues',
  'Refunds',
  'Event info',
  'Account help',
  'Organizer help',
  'Technical issues',
  'Billing / Payment',
  'Other',
];

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm Vibana's support assistant \n\nHow can I help you today? Ask me anything about tickets, refunds, events, your account, or organizer tools.",
};
const END_CHAT_MESSAGE = 'Your session has ended. Thanks for chatting with us! If you need help again, just tap the support button.';

const KEYWORD_MAP = [
  { pattern: /refund|cancel|money back|charged twice/i, category: 'Refunds', priority: 'High' },
  { pattern: /crash|bug|broken|not working|error/i, category: 'Technical Issues', priority: 'Medium' },
  { pattern: /account|login|password|locked|access/i, category: 'Account Help', priority: 'High' },
  { pattern: /ticket|qr code|download/i, category: 'Ticket Issues', priority: 'Medium' },
];

function classifyMessage(text) {
  for (const { pattern, category, priority } of KEYWORD_MAP) {
    if (pattern.test(text)) return { category, priority };
  }
  return { category: 'General Question', priority: 'Medium' };
}

function findAnswer(text) {
  const lower = text.toLowerCase();
  for (const faq of FAQ) {
    if (faq.keywords.some(k => lower.includes(k))) {
      return faq.answer;
    }
  }
  return null;
}

// ─── Sentiment detection ──────────────────────────────────────────────────────
const ANGRY_PATTERN = /frustrat|furious|scam|stolen|ripped off|terrible|worst|awful|!!+/i;
const URGENT_PATTERN = /asap|emergency|immediately|urgent|now|critical/i;

function detectSentiment(text) {
  const isAngry = ANGRY_PATTERN.test(text);
  const isUrgent = URGENT_PATTERN.test(text);
  return { isAngry, isUrgent, isElevated: isAngry || isUrgent };
}

// ─── Simple fetch-based ticket submission (no CSRF — public endpoint) ─────────
async function submitTicket(payload) {
  const res = await fetch(`${API_BASE_URL}/support/tickets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  return res.json();
}

// ─── Component ───────────────────────────────────────────────────────────────
const STEP_CHAT = 'chat';
const STEP_COLLECT = 'collect';
const STEP_DONE = 'done';
const STEP_REVIEW = 'review';
const NO_REPLY_PATTERN = /^no\b|nope|nah|not really|i'm good|im good|all good|that's all|thats all|nothing else/i;
const YES_REPLY_PATTERN = /^yes\b|yeah|yep|sure|actually|yes please/i;
const REVIEW_PROMPT = "Thank you for visiting Vibana today!\n\nWe'd love to hear about your experience. How would you rate your support chat today?\n\n1 — Poor\n2 — Fair\n3 — Good\n4 — Great\n5 — Excellent\n\nJust reply with a number from 1 to 5!";

export default function SupportChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEP_CHAT);
  const [messages, setMessages] = useState(() => {
    try {
      if (typeof localStorage === 'undefined') return [WELCOME_MESSAGE];
      const saved = localStorage.getItem('vb_chat_history');
      if (!saved) return [WELCOME_MESSAGE];
      const parsed = JSON.parse(saved);
      // G: localStorage only stores assistant messages + count; reconstruct safely
      if (parsed && parsed._assistantOnly) {
        return [WELCOME_MESSAGE, ...(parsed.assistantMessages || [])];
      }
      return Array.isArray(parsed) ? parsed : [WELCOME_MESSAGE];
    } catch {
      return [WELCOME_MESSAGE];
    }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [ticketData, setTicketData] = useState({ name: '', email: '', category: '', issue: '', priority: 'Medium', linkedTicketId: '' });
  const [collectField, setCollectField] = useState('name');
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [userTickets, setUserTickets] = useState([]);
  // D: retry counter for looping form steps (email, rating)
  const [retryCount, setRetryCount] = useState(0);
  // C: clarification flag — set when user sends a very short message with no FAQ match
  const [awaitingClarification, setAwaitingClarification] = useState(false);
  const lastSendRef = useRef(0);
  const endChatTimerRef = useRef(null);

  // Keep a ref of messages for ticket submission so we always have the latest
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Lazy-load user's tickets when chat is first opened — used for pre-fill only
  useEffect(() => {
    if (!open || !user) return;
    getMyTickets()
      .then(r => {
        const tickets = Array.isArray(r.data) ? r.data : [];
        setUserTickets(tickets.filter(t => ['confirmed', 'checked_in', 'pending', 'pending_refund'].includes(t.status)));
      })
      .catch(e => console.warn("[silent catch]", e?.message || e)); // optional — silently ignore
  }, [open, user]);

  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        // G: Only persist assistant messages — never store user-typed content
        const assistantMessages = messages.filter(m => m.role === 'assistant');
        localStorage.setItem('vb_chat_history', JSON.stringify({
          _assistantOnly: true,
          messageCount: messages.length,
          assistantMessages,
        }));
      }
    } catch {}
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    if (endChatTimerRef.current) {
      clearTimeout(endChatTimerRef.current);
      endChatTimerRef.current = null;
    }
    try { localStorage.removeItem('vb_chat_history'); } catch {}
    setMessages([WELCOME_MESSAGE]);
    setStep(STEP_CHAT);
    setInput('');
    setCollectField('name');
    setRetryCount(0);
    setAwaitingClarification(false);
    setLoading(false);
    setTicketData({ name: '', email: '', category: '', issue: '', priority: 'Medium', linkedTicketId: '' });
  }, [open]);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const scrollRef = useRef(null);
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => () => {
    if (endChatTimerRef.current) {
      clearTimeout(endChatTimerRef.current);
    }
  }, []);

  const addMsg = (role, content) =>
    setMessages(prev => [...prev, { role, content }]);

  // ── ticket collection flow ────────────────────────────────────────────────
  const startTicketFlow = (originalQuestion) => {
    const classified = classifyMessage(originalQuestion || '');
    const prefillName = user?.name || '';
    const prefillEmail = user?.email || '';

    // B: Sentiment — elevate priority for angry/urgent messages
    const sentiment = detectSentiment(originalQuestion || '');
    let priority = classified.priority;
    if (sentiment.isElevated) {
      priority = 'High';
    }

    setTicketData({ name: prefillName, email: prefillEmail, category: classified.category, issue: originalQuestion || '', priority, linkedTicketId: '' });

    // B: Show urgency acknowledgement message first if sentiment is elevated
    const urgencyPrefix = sentiment.isElevated
      ? "I can see this is urgent — our team will prioritize your ticket.\n\n"
      : '';

    if (prefillName && prefillEmail) {
      const needsTicketPicker = /refund|cancel|ticket|qr|can't access|can not access|missing|wrong ticket/i.test(originalQuestion || '');
      if (needsTicketPicker && userTickets.length > 0) {
        setCollectField('ticketPicker');
        setStep(STEP_COLLECT);
        const ticketList = userTickets.map((t, i) => `${i + 1}. ${t.event?.title || 'Event'} — ${t.ticketId || t._id}`).join('\n');
        setTimeout(() => addMsg('assistant', `${urgencyPrefix}Hi ${prefillName}! I can see your account. Which ticket is this about?\n\n${ticketList}\n\nType the number or "none of these".`), 300);
      } else {
        setCollectField('issue');
        setStep(STEP_COLLECT);
        setTimeout(() => addMsg('assistant', `${urgencyPrefix}Hi ${prefillName}! I'll create a support ticket for our team.\n\nPlease describe your issue in as much detail as you can.`), 300);
      }
    } else {
      setCollectField('name');
      setStep(STEP_COLLECT);
      setTimeout(() => addMsg('assistant', `${urgencyPrefix}I'd like to create a support ticket for our team.\n\nFirst, what's your name?`), 300);
    }
  };

  const handleCollectStep = async (value) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    if (collectField === 'name') {
      setTicketData(d => ({ ...d, name: trimmed }));
      setCollectField('email');
      addMsg('user', trimmed);
      setTimeout(() => addMsg('assistant', `Thanks ${trimmed}! What email address do you use for your Vibana account?`), 300);
      return;
    }

    if (collectField === 'email') {
      addMsg('user', trimmed);
      const emailOk = /^[\w.-]+@[\w.-]+\.\w+$/.test(trimmed);
      if (!emailOk) {
        const newRetry = retryCount + 1;
        setRetryCount(newRetry);
        if (newRetry >= 3) {
          // D: After 3 invalid emails, move forward with a placeholder
          setRetryCount(0);
          setTicketData(d => ({ ...d, email: 'unknown@vibana.support' }));
          setCollectField('category');
          const catList = TICKET_CATEGORIES.map((c, i) => `${i + 1}. ${c}`).join('\n');
          setTimeout(() => addMsg('assistant', `No worries — we'll follow up another way. What type of issue is this?\n\n${catList}\n\nReply with the number or name.`), 300);
          return;
        }
        setTimeout(() => addMsg('assistant', 'That doesn\'t look like a valid email. Please enter your email address so we can follow up with you.'), 300);
        return;
      }
      setRetryCount(0);
      setTicketData(d => ({ ...d, email: trimmed }));

      const needsTicketPicker = /refund|cancel|ticket|qr|can't access|missing|wrong ticket/i.test(ticketData.issue || '');
      if (needsTicketPicker && userTickets.length > 0) {
        setCollectField('ticketPicker');
        const ticketList = userTickets.map((t, i) => `${i + 1}. ${t.event?.title || 'Event'} — ${t.ticketId || t._id}`).join('\n');
        setTimeout(() => addMsg('assistant', `Got it! Which ticket is this about?\n\n${ticketList}\n\nType the number or "none of these".`), 300);
      } else {
        setCollectField('category');
        const catList = TICKET_CATEGORIES.map((c, i) => `${i + 1}. ${c}`).join('\n');
        setTimeout(() => addMsg('assistant', `Got it! What type of issue is this?\n\n${catList}\n\nReply with the number or name.`), 300);
      }
      return;
    }

    if (collectField === 'ticketPicker') {
      addMsg('user', trimmed);
      const num = parseInt(trimmed, 10);
      if (!isNaN(num) && num >= 1 && num <= userTickets.length) {
        const picked = userTickets[num - 1];
        setTicketData(d => ({ ...d, linkedTicketId: picked.ticketId || picked._id || '', category: d.category || 'Ticket issues' }));
        setCollectField('issue');
        setTimeout(() => addMsg('assistant', `Got it — ${picked.event?.title || 'that ticket'}. Please describe what the issue is with it.`), 300);
      } else {
        setCollectField('category');
        const catList = TICKET_CATEGORIES.map((c, i) => `${i + 1}. ${c}`).join('\n');
        setTimeout(() => addMsg('assistant', `No problem. What category best describes your issue?\n\n${catList}`), 300);
      }
      return;
    }

    if (collectField === 'category') {
      addMsg('user', trimmed);
      // Accept number or partial name
      const num = parseInt(trimmed, 10);
      let cat = null;
      if (num >= 1 && num <= TICKET_CATEGORIES.length) {
        cat = TICKET_CATEGORIES[num - 1];
      } else {
        cat = TICKET_CATEGORIES.find(c => c.toLowerCase().includes(trimmed.toLowerCase()));
      }
      if (!cat) {
        setTimeout(() => addMsg('assistant', 'Please reply with a number (1–8) or the name of the category.'), 300);
        return;
      }
      setTicketData(d => ({ ...d, category: cat }));
      setCollectField('issue');
      setTimeout(() => addMsg('assistant', 'Almost done — please describe your issue in as much detail as you can.'), 300);
      return;
    }

    if (collectField === 'issue') {
      addMsg('user', trimmed);
      setLoading(true);

      // Snapshot the full conversation including this last message
      const finalConversation = [
        ...messagesRef.current,
        { role: 'user', content: trimmed },
      ];
      const conversationHistory = finalConversation.slice(-8);
      const classified = classifyMessage(trimmed);

      const finalData = { ...ticketData, issue: trimmed };

      try {
        await submitTicket({
          category: finalData.category || classified.category,
          priority: finalData.priority || classified.priority,
          summary: `${finalData.category || classified.category} — ${trimmed.slice(0, 80)}`,
          userEmail: finalData.email,
          userName: finalData.name,
          linkedTicketId: finalData.linkedTicketId || undefined,
          source: 'web_widget',
          conversation: conversationHistory.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: new Date().toISOString(),
          })),
        });
        setStep(STEP_DONE);
        setTimeout(() => addMsg('assistant', `Ticket submitted! Our team will follow up at ${finalData.email} within 24 hours.\n\nIs there anything else I can help you with?`), 300);
      } catch (err) {
        setStep(STEP_DONE);
        setTimeout(() => addMsg('assistant', `We had trouble submitting your ticket automatically. Please email us directly at support@vibana.app and mention:\n\n${finalData.category} — ${trimmed.slice(0, 80)}`), 300);
      }

      setLoading(false);
    }
  };

  const handleDoneStep = (text) => {
    addMsg('user', text);
    if (NO_REPLY_PATTERN.test(text)) {
      setStep(STEP_REVIEW);
      setTimeout(() => addMsg('assistant', REVIEW_PROMPT), 300);
      return;
    }
    setStep(STEP_CHAT);
    if (YES_REPLY_PATTERN.test(text)) {
      setTimeout(() => addMsg('assistant', 'Of course! What else can I help you with?'), 300);
      return;
    }
    setTimeout(() => addMsg('assistant', 'What else can I help you with?'), 300);
  };

  const handleReviewStep = (text) => {
    addMsg('user', text);
    const trimmed = text.trim();
    if (!/^[1-5]$/.test(trimmed)) {
      const newRetry = retryCount + 1;
      setRetryCount(newRetry);
      if (newRetry >= 3) {
        // D: After 3 invalid inputs, gracefully skip the rating step
        setRetryCount(0);
        addMsg('assistant', 'No worries — we appreciate you chatting with us today! ');
        addMsg('assistant', 'Take care and enjoy your event!');
        setStep(STEP_CHAT);
        setTimeout(() => addMsg('assistant', WELCOME_MESSAGE.content), 300);
        return;
      }
      setTimeout(() => addMsg('assistant', 'Please reply with a number from 1 to 5.'), 300);
      return;
    }
    setRetryCount(0);
    const rating = Number(trimmed);
    if (rating <= 2) {
      addMsg('assistant', "We're sorry to hear that. Your feedback helps us improve — thank you for taking the time. ");
    } else if (rating === 3) {
      addMsg('assistant', "Thanks for the feedback! We're always working to improve. ");
    } else {
      addMsg('assistant', "That's wonderful, thank you! ★ We're glad we could help.");
    }
    addMsg('assistant', 'Take care and enjoy your event! ');
    setStep(STEP_CHAT);
    setTimeout(() => addMsg('assistant', WELCOME_MESSAGE.content), 300);
  };

  const sendMessage = async () => {
    if (Date.now() - lastSendRef.current < 2000) return;
    lastSendRef.current = Date.now();
    const text = input.trim();
    if (!text || loading || !isOnline) return;
    setInput('');

    if (step === STEP_COLLECT) {
      handleCollectStep(text);
      return;
    }

    if (step === STEP_DONE) {
      handleDoneStep(text);
      return;
    }

    if (step === STEP_REVIEW) {
      handleReviewStep(text);
      return;
    }

    // Explicit ticket request
    if (/\bticket\b|create ticket|open ticket|human|speak to|talk to|agent/i.test(text)) {
      addMsg('user', text);
      setAwaitingClarification(false);
      startTicketFlow(text);
      return;
    }

    addMsg('user', text);
    const answer = findAnswer(text);
    if (answer) {
      setAwaitingClarification(false);
      setTimeout(() => addMsg('assistant', answer + '\n\n—\nDid that help? If you still need support type "ticket" and I\'ll create one for you.'), 300);
    } else if (text.length < 10 && !awaitingClarification) {
      // C: Short message with no FAQ match — ask for clarification before escalating
      setAwaitingClarification(true);
      setTimeout(() => addMsg('assistant', 'Could you tell me a bit more about what you need help with?'), 300);
    } else {
      // Either we were already waiting for clarification, or message is long enough
      setAwaitingClarification(false);
      setTimeout(() => {
        addMsg('assistant', "I don't have a ready answer for that — let me create a support ticket so our team can help you directly.");
        startTicketFlow(text);
      }, 400);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const closeChat = () => {
    setOpen(false);
  };

  const endChat = () => {
    if (endChatTimerRef.current) return;
    setLoading(false);
    addMsg('assistant', END_CHAT_MESSAGE);
    try { localStorage.removeItem('vb_chat_history'); } catch {}
    endChatTimerRef.current = setTimeout(() => {
      setOpen(false);
      endChatTimerRef.current = null;
    }, 1500);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Support"
        aria-label={open ? 'Close support chat' : 'Open support chat'}
        style={{
          position: 'fixed', bottom: 28, right: 24, zIndex: 9999,
          width: 56, height: 56, borderRadius: '50%', border: 'none',
          background: open
            ? '#151210'
            : 'linear-gradient(135deg, #E63946 0%, #c92d3a 100%)',
          cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          boxShadow: open
            ? '0 4px 16px rgba(0,0,0,0.4)'
            : '0 6px 24px rgba(230,57,70,0.5), 0 0 0 0 rgba(230,57,70,0.4)',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: open ? 'none' : 'vibana-fab-pulse 2.4s ease-in-out infinite',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.08)';
          e.currentTarget.style.boxShadow = '0 8px 28px rgba(230,57,70,0.6)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = open
            ? '0 4px 16px rgba(0,0,0,0.4)'
            : '0 6px 24px rgba(230,57,70,0.5)';
        }}
      >
        <style>{`
          @keyframes vibana-fab-pulse {
            0%, 100% { box-shadow: 0 6px 24px rgba(230,57,70,0.5), 0 0 0 0 rgba(230,57,70,0.5); }
            50%      { box-shadow: 0 6px 24px rgba(230,57,70,0.5), 0 0 0 10px rgba(230,57,70,0); }
          }
        `}</style>
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 96, right: 24, zIndex: 9998,
          width: 380, height: 560, background: '#0A0908', borderRadius: 18,
          border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 70px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
          overflow: 'hidden',
          animation: 'vibana-chat-in 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          <style>{`
            @keyframes vibana-chat-in {
              from { opacity: 0; transform: translateY(20px) scale(0.96); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
            @keyframes vibana-msg-in {
              from { opacity: 0; transform: translateY(6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            @keyframes vibana-status-pulse {
              0%, 100% { opacity: 1; }
              50%      { opacity: 0.5; }
            }
            .vb-chat-scroll::-webkit-scrollbar { width: 6px; }
            .vb-chat-scroll::-webkit-scrollbar-track { background: transparent; }
            .vb-chat-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 3px; }
            .vb-chat-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.15); }
            .vb-chat-input:focus { border-color: rgba(230,57,70,0.5) !important; background: rgba(255,255,255,0.08) !important; }
          `}</style>

          {/* Header */}
          <div style={{
            padding: '14px 16px',
            background: 'linear-gradient(180deg, #17130E 0%, #121010 100%)',
            borderBottom: '1px solid rgba(217,168,59,0.12)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%',
                background: 'linear-gradient(135deg, #E63946 0%, #c92d3a 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900, fontSize: 16, color: '#fff',
                boxShadow: '0 2px 8px rgba(230,57,70,0.4)',
              }}>V</div>
              <div style={{
                position: 'absolute', bottom: -1, right: -1,
                width: 11, height: 11, borderRadius: '50%',
                background: isOnline ? '#10b981' : '#6b7280',
                border: '2px solid #0A0908',
                animation: isOnline ? 'vibana-status-pulse 2s ease-in-out infinite' : 'none',
              }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#FAF7F2', letterSpacing: 0.1 }}>Vibana Support</div>
              <div style={{ fontSize: 11, color: 'rgba(250,247,242,0.45)', marginTop: 1 }}>
                {isOnline ? 'Online · typically replies instantly' : 'Offline — reconnecting…'}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={endChat}
                style={{
                  height: 26,
                  borderRadius: 8,
                  border: '1px solid rgba(250,247,242,0.15)',
                  background: 'transparent',
                  color: 'rgba(250,247,242,0.7)',
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '0 10px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  e.currentTarget.style.color = '#FAF7F2';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(250,247,242,0.7)';
                }}
              >
                End Chat
              </button>
              <button
                onClick={closeChat}
                aria-label="Close"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'rgba(250,247,242,0.5)', fontSize: 18, lineHeight: 1,
                  padding: 6, borderRadius: 6, transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.color = '#FAF7F2';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = 'rgba(250,247,242,0.5)';
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="vb-chat-scroll"
            style={{
              flex: 1, overflowY: 'auto', padding: '16px 14px',
              display: 'flex', flexDirection: 'column', gap: 8,
              background: 'radial-gradient(ellipse at top, rgba(217,168,59,0.05) 0%, transparent 60%), #0A0908',
            }}
          >
            {messages.map((msg, i) => {
              const isUser = msg.role === 'user';
              const prev = messages[i - 1];
              const grouped = prev && prev.role === msg.role;
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: isUser ? 'flex-end' : 'flex-start',
                    marginTop: grouped ? 2 : 6,
                    animation: 'vibana-msg-in 0.22s ease-out',
                  }}
                >
                  <div style={{
                    maxWidth: '82%',
                    padding: '10px 14px',
                    borderRadius: 16,
                    borderTopRightRadius: isUser && !grouped ? 4 : 16,
                    borderBottomRightRadius: isUser ? 16 : 16,
                    borderTopLeftRadius: !isUser && !grouped ? 4 : 16,
                    background: isUser
                      ? 'linear-gradient(135deg, rgba(230,57,70,0.22) 0%, rgba(230,57,70,0.14) 100%)'
                      : 'rgba(255,255,255,0.055)',
                    border: `1px solid ${isUser ? 'rgba(230,57,70,0.25)' : 'rgba(255,255,255,0.07)'}`,
                    color: isUser ? '#FAF7F2' : 'rgba(250,247,242,0.92)',
                    fontSize: 13.5,
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    boxShadow: isUser ? '0 1px 2px rgba(0,0,0,0.15)' : 'none',
                  }}>
                    {msg.content}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 6, animation: 'vibana-msg-in 0.22s ease-out' }}>
                <div style={{
                  padding: '12px 14px', borderRadius: '16px 16px 16px 4px',
                  background: 'rgba(255,255,255,0.055)', border: '1px solid rgba(255,255,255,0.07)',
                  display: 'flex', alignItems: 'center',
                }}>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {[0, 1, 2].map(i => (
                      <div key={i} style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: 'rgba(250,247,242,0.6)',
                        animation: 'vibana-dot 1.2s infinite ease-in-out',
                        animationDelay: `${i * 0.18}s`,
                      }} />
                    ))}
                  </div>
                  <style>{`
                    @keyframes vibana-dot {
                      0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
                      40% { transform: translateY(-4px); opacity: 1; }
                    }
                  `}</style>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 14px',
            borderTop: '1px solid rgba(217,168,59,0.12)',
            background: 'linear-gradient(180deg, #121010 0%, #0D0B09 100%)',
          }}>
            {!isOnline && (
              <div style={{
                marginBottom: 8,
                background: 'rgba(230,57,70,0.15)',
                border: '1px solid rgba(230,57,70,0.35)',
                color: '#FAF7F2',
                borderRadius: 8,
                padding: '7px 10px',
                fontSize: 11,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E63946', display: 'inline-block' }} />
                No internet connection
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              className="vb-chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={step === STEP_COLLECT ? 'Type your answer…' : 'Type your message…'}
              rows={1}
              maxLength={500}
              disabled={!isOnline}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12, padding: '10px 14px', color: '#FAF7F2', fontSize: 13.5,
                resize: 'none', outline: 'none', fontFamily: 'inherit',
                maxHeight: 90, overflowY: 'auto',
                transition: 'all 0.15s',
                lineHeight: 1.5,
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading || !isOnline}
              aria-label="Send message"
              style={{
                width: 38, height: 38, borderRadius: 12, border: 'none',
                background: input.trim() && isOnline
                  ? 'linear-gradient(135deg, #E63946 0%, #c92d3a 100%)'
                  : 'rgba(230,57,70,0.2)',
                color: '#fff',
                cursor: input.trim() && isOnline ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                boxShadow: input.trim() && isOnline ? '0 2px 8px rgba(230,57,70,0.35)' : 'none',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                if (input.trim() && isOnline) e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5"/>
                <polyline points="5 12 12 5 19 12"/>
              </svg>
            </button>
            </div>
            <div style={{
              marginTop: 6, fontSize: 10, color: 'rgba(250,247,242,0.3)',
              textAlign: 'center', letterSpacing: 0.3,
            }}>
              Powered by Vibana · Press Enter to send
            </div>
          </div>
        </div>
      )}
    </>
  );
}
